import { useTranslation } from "react-i18next";
import {
  MINUTE_MS,
  formatClockOfDay,
  formatMinuteOfDay,
  minuteOfDay,
  toDateKey,
  type TimelineRange,
} from "../logic/dayTime";
import type { TimelineMarker } from "../logic/momentFocus";
import { useReservationFormat } from "../hooks/useReservationFormat";
import TimelineTrack from "./TimelineTrack";

/**
 * Времевата линия над схемата: „СЕГА / ПРЕГЛЕД КЪМ / ПО-РАНО" + часа (в
 * прегледа — оранжев, с „след 1 ч 35 мин" / „преди 40 мин" спрямо сега),
 * пистата (`TimelineTrack`: натовареност, маркери на резервациите, чертичка
 * „сега", плъзгане) и „● Сега" (активен само извън „сега"). Плъзгането мести
 * момента, по който платното, табовете на регионите и дясната колона
 * показват масите и резервациите. Обхватът е на деня (`range`): от 11:00 или
 * от най-ранното на деня до 24:00, надписите — адаптивни. Момент извън обхвата
 * (по изключение): часът вляво е истинският, а палецът на ръба е кух с
 * надпис „извън 11:00–24:00" — да не изглежда, че е 11:00.
 */
const Timeline = ({
  momentMs,
  dateKey,
  nowMs,
  sliderMinute,
  range,
  isOutside,
  isAtNow,
  isNarrow,
  markers,
  load,
  onChange,
  onNow,
}: {
  momentMs: number;
  // Разглежданият ден — краят му (24:00) е 00:00 на следващия по календар.
  dateKey: string;
  nowMs: number;
  sliderMinute: number;
  range: TimelineRange;
  // Моментът е извън обхвата (палецът стои на ръба).
  isOutside: boolean;
  isAtNow: boolean;
  isNarrow: boolean;
  // Началата на резервациите на деня (точките под пистата).
  markers: TimelineMarker[];
  // Гостите на масите по 15-минутни слотове (графиката над пистата).
  load: number[];
  onChange: (minute: number) => void;
  onNow: () => void;
}) => {
  const { t } = useTranslation();
  const format = useReservationFormat();
  // По минути: закованият час е началото на минутата, а „сега" е със секундите.
  const momentMinute = Math.floor(momentMs / MINUTE_MS);
  const nowMinute = Math.floor(nowMs / MINUTE_MS);
  const label = isAtNow || momentMinute === nowMinute
    ? t("reservations.timeline.now")
    : momentMinute > nowMinute
      ? t("reservations.timeline.preview")
      : t("reservations.timeline.earlier");
  const isPreview = !isAtNow && momentMinute !== nowMinute;
  // „след 1 ч 35 мин" / „преди 40 мин" — само в същия ден като „сега".
  // По разглеждания ден, не по календарния на момента: 24:00 е 00:00 утре.
  const isSameDay = dateKey === toDateKey(new Date(nowMs));
  const clock = formatClockOfDay(momentMs, dateKey);
  const offsetMinutes = momentMinute - nowMinute;
  const offset = isPreview && isSameDay
    ? t(offsetMinutes > 0 ? "reservations.timeline.inFuture" : "reservations.timeline.inPast", {
        duration: format.duration(Math.abs(offsetMinutes)),
      })
    : null;
  // Чертичката „сега" на пистата — в прегледа на днешния ден.
  // Магнитът „сега" работи винаги в днешния ден — и на живо (докосване на
  // палеца без местене не бива да вади от „сега").
  const liveMinute = isSameDay ? minuteOfDay(nowMs) : null;
  const nowOnTrack = isPreview ? liveMinute : null;

  return (
    <div
      className={`flex shrink-0 items-center border-b border-shade-2 bg-surface ${
        isNarrow ? "gap-3 px-3.5 py-2.5" : "gap-6 px-[18px] py-3"
      }`}
    >
      <div className={`shrink-0 ${isNarrow ? "w-[84px]" : "w-[112px]"}`}>
        <div className="truncate text-[10.5px] font-bold uppercase leading-[1.2] tracking-[1.2px] text-muted">
          {label}
        </div>
        <div
          className={`mt-0.5 font-bold leading-[1.1] tracking-[-0.6px] tabular-nums transition-colors duration-200 ${
            isPreview ? "text-brand-deep-ink" : "text-ink"
          } ${isNarrow ? "text-[22px]" : "text-[26px]"}`}
        >
          {clock}
        </div>
        {offset && !isOutside && (
          <div className="truncate text-[11px] font-semibold leading-[1.3] tabular-nums text-muted">{offset}</div>
        )}
        {isOutside && (
          <div className="truncate text-[10.5px] font-semibold leading-[1.2] text-muted">
            {t("reservations.timeline.outside", {
              from: formatMinuteOfDay(range.startMinute),
              to: formatMinuteOfDay(range.endMinute),
            })}
          </div>
        )}
      </div>

      <TimelineTrack
        sliderMinute={sliderMinute}
        range={range}
        isOutside={isOutside}
        nowMinute={nowOnTrack}
        liveMinute={liveMinute}
        markers={markers}
        load={load}
        valueText={clock}
        onChange={onChange}
      />

      <button
        type="button"
        onClick={onNow}
        disabled={isAtNow}
        className={`flex h-9 shrink-0 items-center gap-2 rounded-[9px] px-3.5 text-[13px] font-bold transition-colors duration-150 ${
          isAtNow
            ? "cursor-default border border-shade-3 bg-fill text-muted"
            : "cursor-pointer border border-brand bg-brand text-white hover:bg-brand-deep"
        }`}
      >
        <span className={`size-1.5 rounded-full ${isAtNow ? "bg-muted" : "bg-white"}`} />
        {t("reservations.timeline.backToNow")}
      </button>
    </div>
  );
};

export default Timeline;
